import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { permissionsData } from './outlineData.model';
import { env, python, envPython } from 'src/environments/environments';

@Injectable({
  providedIn: 'root'
})
export class outlineService {
  private readonly fastapi_url_base = `${envPython.url_base}`;

  private permisos: any = {};
  private elementos: any[] = [];

  constructor(private http: HttpClient) { }

  cargarPermisos(idPerfil: number | undefined): Observable<{ success: boolean; message: string; data?: any }> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.get<{ success: boolean; message: string; data: any }>(
      `${this.fastapi_url_base}/consultarPermisosVistas/${idPerfil}`, { headers }
    ).pipe(
      map(response => {
        if (response && response.success) {
          this.permisos = this.procesarPermisos(response.data);
          return { success: true, message: 'Permisos cargados correctamente', data: this.permisos };
        } else {
          return { success: false, message: 'No se encontraron permisos', data: [] };
        }
      }),
      catchError(error => {
        console.error('API error:', error);
        return of({ success: false, message: error.message, data: [] });
      })
    );
  }

  // Procesar los permisos para organizarlos por módulo y vista
  private procesarPermisos(data: permissionsData[]): any {
    const permisosProcesados: any = {};

    data.forEach(permiso => {
      const { id_modulo, id_vista, tipo, permiso: permisoTipo } = permiso;

      if (!permisosProcesados[id_modulo]) {
        permisosProcesados[id_modulo] = {};
      }

      if (!permisosProcesados[id_modulo][id_vista]) {
        permisosProcesados[id_modulo][id_vista] = {};
      }

      permisosProcesados[id_modulo][id_vista][permisoTipo] = {
        id_modulo,
        id_vista,
        tipo,
        permiso: permisoTipo
      };
    });

    return permisosProcesados;
  }

  // Verificar si el usuario tiene permiso para acceder a una vista específica
  tienePermiso(id_modulo: number, id_vista: number, permisoRequerido: string): boolean {
    const permiso = this.permisos[id_modulo]?.[id_vista]?.[permisoRequerido];
    if (permiso === undefined) {
      return true;
    }
    return false;
  }

  // Cargar elementos (podrían ser campos o componentes) y procesarlos
  cargarElementos(idPerfil: number | undefined): Observable<{ success: boolean; message: string; data?: any }> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.get<{ success: boolean; message: string; data: any }>(
      `${this.fastapi_url_base}/consultarElementosPerfil/${idPerfil}`, { headers }
    ).pipe(
      map(response => {
        if (response && response.success) {
          this.elementos = response.data;
          return { success: true, message: 'Elementos cargados correctamente', data: response.data };
        } else {
          return { success: false, message: 'No se encontraron elementos', data: [] };
        }
      }),
      catchError(error => {
        console.error('API error:', error);
        return of({ success: false, message: error.message, data: [] });
      })
    );
  }

  // Obtener los elementos almacenados
  getElementos(): any[] {
    return this.elementos;
  }

  aplicarAccion(id_modulo: number, id_vista: number, id_elemento: number): 'hide' | 'block' | 'obfuscate' | null {
    // Buscar el elemento correspondiente en la lista de elementos cargados
    const elemento = this.elementos.find(
      elem => elem.id_modulo === id_modulo && elem.id_vista === id_vista && elem.id_elemento === id_elemento
    );

    if (elemento) {
      // Basado en el permiso del elemento, aplicamos la acción correspondiente
      switch (elemento.permiso) {
        case 'Ocultar':
          return 'hide';
        case 'Bloquear':
          return 'block';
        case 'Ofuscar':
          return 'obfuscate';
        default:
          return null; // Si no hay una acción definida, no se aplica nada.
      }
    } else {
      return null;  // No se encontró un elemento correspondiente, no se aplica ninguna acción
    }
  }

  filtrarElementosPorId(id_elemento: number | undefined): any[] {
    if (id_elemento === undefined) return []; // Devuelve un array vacío si el id es undefined
    return this.elementos.filter(elemento => elemento.id_elemento === id_elemento);
  }

  // MÉTODOS NUEVOS PARA INVALIDAR CACHE

  // Limpiar cache completamente
  invalidarCache(): void {
    this.permisos = {};
    this.elementos = [];
  }

  // Recargar datos completos para un perfil específico
  recargarDatos(idPerfil: number): Observable<{ success: boolean; message: string; data?: any }> {
    this.invalidarCache();

    // Cargar permisos y elementos en paralelo
    return new Observable(observer => {
      let permisosCompletos = false;
      let elementosCompletos = false;
      let permisosExitosos = false;
      let elementosExitosos = false;

      const verificarCompletado = () => {
        if (permisosCompletos && elementosCompletos) {
          // Considerar exitoso si al menos uno de los dos fue exitoso
          if (permisosExitosos || elementosExitosos) {
            observer.next({
              success: true,
              message: 'Datos recargados correctamente',
              data: { permisos: this.permisos, elementos: this.elementos }
            });
            observer.complete();
          } else {
            // Solo fallar si ambos fallaron
            observer.error({
              success: false,
              message: 'Error al recargar todos los datos',
              data: []
            });
          }
        }
      };

      // Cargar permisos
      this.cargarPermisos(idPerfil).subscribe({
        next: (response) => {
          permisosCompletos = true;
          permisosExitosos = response.success;
          verificarCompletado();
        },
        error: (error) => {
          permisosCompletos = true;
          permisosExitosos = false;
          verificarCompletado();
        }
      });

      // Cargar elementos
      this.cargarElementos(idPerfil).subscribe({
        next: (response) => {
          elementosCompletos = true;
          elementosExitosos = response.success;
          verificarCompletado();
        },
        error: (error) => {
          elementosCompletos = true;
          elementosExitosos = false;
          verificarCompletado();
        }
      });
    });
  }

  // Método para verificar si el cache está vacío
  cacheVacio(): boolean {
    return Object.keys(this.permisos).length === 0 && this.elementos.length === 0;
  }
}
